import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';

const AudioTrack = forwardRef(({ sound, index, globalPlaying, onGlobalPlayStateChange }, ref) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [waveformData, setWaveformData] = useState([]);
  const audioRef = useRef(null);
  const canvasRef = useRef(null);

  useImperativeHandle(ref, () => ({
    play: () => {
      const audio = audioRef.current;
      if (audio && isLoaded) {
        audio.play();
        setIsPlaying(true);
      }
    },
    pause: () => {
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
        setIsPlaying(false);
      }
    },
    isLoaded
  }));

  // Generate blocky waveform data
  const generateWaveform = async () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const response = await fetch(sound.path);
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      
      const samples = audioBuffer.getChannelData(0);
      const blockSize = Math.floor(samples.length / 80); // 80 blocks for retro look
      const blocks = [];
      
      for (let i = 0; i < 80; i++) {
        let sum = 0;
        const start = i * blockSize;
        const end = Math.min(start + blockSize, samples.length);
        
        for (let j = start; j < end; j++) {
          sum += Math.abs(samples[j]);
        }
        
        blocks.push(sum / (end - start));
      }
      
      // Normalize to 0-1 range
      const max = Math.max(...blocks);
      const normalized = blocks.map(block => block / max);
      
      setWaveformData(normalized);
      audioContext.close();
    } catch (error) {
      console.error('Error generating waveform:', error);
      // Fallback to random data for demo
      const fallback = Array.from({ length: 80 }, () => Math.random() * 0.5 + 0.1);
      setWaveformData(fallback);
    }
  };

  // Draw blocky waveform
  const drawWaveform = () => {
    const canvas = canvasRef.current;
    if (!canvas || waveformData.length === 0) return;
    
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    const blockWidth = width / waveformData.length; // Use exact division instead of Math.floor
    const centerY = height / 2;
    
    waveformData.forEach((amplitude, index) => {
      const x = index * blockWidth;
      const blockHeight = Math.max(amplitude * centerY * 0.8, 2); // Min height of 2px
      
      // Draw blocky bars with retro styling
      ctx.fillStyle = isPlaying ? '#000000' : '#666666';
      ctx.fillRect(x + 1, centerY - blockHeight/2, Math.max(blockWidth - 2, 1), blockHeight);
    });
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      const handleCanPlay = () => {
        setIsLoaded(true);
        generateWaveform();
      };
      const handleEnded = () => {
        setIsPlaying(false);
        if (onGlobalPlayStateChange) onGlobalPlayStateChange(false);
      };
      const handleError = (e) => {
        console.error('Audio load error:', e);
        setIsLoaded(false);
      };
      
      audio.addEventListener('canplaythrough', handleCanPlay);
      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('error', handleError);
      
      return () => {
        audio.removeEventListener('canplaythrough', handleCanPlay);
        audio.removeEventListener('ended', handleEnded);
        audio.removeEventListener('error', handleError);
      };
    }
  }, [sound, onGlobalPlayStateChange]);

  // Redraw waveform when data changes or playing state changes
  useEffect(() => {
    drawWaveform();
  }, [waveformData, isPlaying]);

  // Handle canvas resize
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const resizeCanvas = () => {
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * window.devicePixelRatio;
        canvas.height = rect.height * window.devicePixelRatio;
        const ctx = canvas.getContext('2d');
        ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
        drawWaveform();
      };
      
      // Use timeout to ensure layout is complete
      const timeoutId = setTimeout(resizeCanvas, 100);
      window.addEventListener('resize', resizeCanvas);
      
      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener('resize', resizeCanvas);
      };
    }
  }, [waveformData]);

  // Handle global play state changes
  useEffect(() => {
    if (globalPlaying !== null) {
      if (globalPlaying && !isPlaying) {
        const audio = audioRef.current;
        if (audio && isLoaded) {
          audio.play();
          setIsPlaying(true);
        }
      } else if (!globalPlaying && isPlaying) {
        const audio = audioRef.current;
        if (audio) {
          audio.pause();
          setIsPlaying(false);
        }
      }
    }
  }, [globalPlaying, isPlaying, isLoaded]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio || !isLoaded) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
      if (onGlobalPlayStateChange) onGlobalPlayStateChange(false);
    } else {
      audio.play();
      setIsPlaying(true);
    }
  };

  const getDisplayName = () => {
    return `${sound.type} ${sound.number} (${sound.category})`;
  };

  const getTypeColor = () => {
    const colors = {
      'AM': 'bg-gray-700',
      'FL': 'bg-gray-600',
      'FX': 'bg-gray-800',
      'GL': 'bg-gray-500',
      'IN': 'bg-gray-400',
      'PD': 'bg-gray-300',
      'PN': 'bg-gray-900',
      'SB': 'bg-black'
    };
    return colors[sound.type] || 'bg-gray-500';
  };

  return (
    <div className="w-full bg-white border-4 border-black p-2 mb-3 scribble-shadow">
      <audio ref={audioRef} preload="metadata" crossOrigin="anonymous">
        <source src={sound.path} type="audio/wav" />
        <source src={sound.path} type="audio/mpeg" />
        Your browser does not support the audio element.
      </audio>
      
      {/* Waveform and play button row */}
      <div className="flex items-center space-x-3">
        <div className={`w-4 h-4 border-2 border-black ${getTypeColor()}`}></div>
        <button
          onClick={togglePlay}
          disabled={!isLoaded}
          className={`w-10 h-10 border-2 border-black flex items-center justify-center transition-all scribble-shadow scribble-shadow-hover scribble-shadow-active flex-shrink-0 ${
            isLoaded 
              ? 'bg-gray-400 hover:bg-gray-300 text-black' 
              : 'bg-gray-200 text-gray-500 cursor-not-allowed'
          }`}
        >
          {!isLoaded ? (
            <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
          ) : isPlaying ? (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z"/>
            </svg>
          )}
        </button>
        
        <div className="flex-1 border-2 border-black bg-gray-100 h-10">
          <canvas
            ref={canvasRef}
            className="w-full h-full"
            style={{ imageRendering: 'pixelated' }}
          />
        </div>
      </div>
    </div>
  );
});

export default AudioTrack;